import { Component } from '@theme/component';
import { isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} BnplStripRefs
 * @property {HTMLElement} [scroller]
 * @property {HTMLElement} [progress]
 * @property {HTMLElement} [thumb]
 */

/**
 * BNPL card strip (KSUB-906). Drives the mobile carousel's scroll progress bar;
 * layout and snapping are CSS. Same thumb maths as the theme's product-list carousel.
 *
 * @extends {Component<BnplStripRefs>}
 */
class BnplStrip extends Component {
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  connectedCallback() {
    super.connectedCallback();
    const { scroller } = this.refs;
    if (!scroller) return;

    scroller.addEventListener('scroll', this.#update, { passive: true });
    this.#resizeObserver = new ResizeObserver(this.#update);
    this.#resizeObserver.observe(scroller);
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.scroller?.removeEventListener('scroll', this.#update);
    this.#resizeObserver?.disconnect();
  }

  #update = () => {
    const { scroller, progress, thumb } = this.refs;
    if (!scroller || !progress || !thumb) return;

    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    if (maxScroll <= 1) {
      progress.hidden = true;
      return;
    }

    progress.hidden = false;
    const trackWidth = progress.clientWidth;
    const thumbWidth = Math.min(trackWidth, Math.max((scroller.clientWidth / scroller.scrollWidth) * trackWidth, 16));
    const offset = (scroller.scrollLeft / maxScroll) * (trackWidth - thumbWidth);

    thumb.style.width = `${thumbWidth}px`;
    thumb.style.transform = `translateX(${offset}px)`;
  };
}

/**
 * @typedef {Object} BnplDialogRefs
 * @property {HTMLDialogElement} dialog
 */

/**
 * Per-provider disclosure dialog. Opens as a plain native modal and never uses the
 * theme's `DialogComponent` body lock (`body { position: fixed }` + `#header-group.drawer-open`),
 * which is built for side drawers and shifts the page behind a centred modal. Background
 * scroll is locked with `html.bnpl-dialog-open` instead, except when nested inside the cart
 * drawer, whose own lock is left untouched.
 *
 * @extends {Component<BnplDialogRefs>}
 */
class BnplDialog extends Component {
  requiredRefs = ['dialog'];

  #closing = false;
  #locked = false;
  /** @type {Element | null} */
  #opener = null;

  showDialog() {
    const { dialog } = this.refs;
    if (dialog.open) return;

    this.#opener = document.activeElement;
    this.#locked = !this.closest('dialog[open]');
    if (this.#locked) document.documentElement.classList.add('bnpl-dialog-open');

    dialog.showModal();
    dialog.addEventListener('cancel', this.#onCancel);
    dialog.addEventListener('keydown', this.#onKeydown);
    dialog.addEventListener('click', this.#onClick);
  }

  closeDialog = async () => {
    const { dialog } = this.refs;
    if (!dialog.open || this.#closing) return;
    this.#closing = true;

    dialog.removeEventListener('cancel', this.#onCancel);
    dialog.removeEventListener('keydown', this.#onKeydown);
    dialog.removeEventListener('click', this.#onClick);

    dialog.classList.add('dialog-closing');
    await onAnimationEnd(dialog, undefined, { subtree: false });
    dialog.close();
    dialog.classList.remove('dialog-closing');

    if (this.#locked) document.documentElement.classList.remove('bnpl-dialog-open');
    this.#locked = false;
    this.#closing = false;

    if (this.#opener instanceof HTMLElement && this.#opener.isConnected) this.#opener.focus();
    this.#opener = null;
  };

  /** @param {Event} event */
  #onCancel = (event) => {
    event.preventDefault();
    this.closeDialog();
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    // Keep Escape from reaching the cart drawer's own handler.
    event.preventDefault();
    event.stopPropagation();
    this.closeDialog();
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!isClickedOutside(event, this.refs.dialog)) return;
    // Backdrop click: close this dialog only, not the drawer beneath it.
    event.stopPropagation();
    this.closeDialog();
  };
}

if (!customElements.get('bnpl-strip')) {
  customElements.define('bnpl-strip', BnplStrip);
}

if (!customElements.get('bnpl-dialog')) {
  customElements.define('bnpl-dialog', BnplDialog);
}
